// ─────────────────────────────────────────────────────────────
// TERROR PROPHECY ENGINE
// Checks all Prophecy win conditions at every half-turn hand-off
// (src/turnEngine.js — it used to be "on every game state update",
// which meant a React listener, which meant only on a mounted screen).
// When the condition and the fare are both there it CHARGES the Khao
// and ends the Solrend — or, in a 3-4p FFA / 2v2, knocks out the seat
// or team the Prophecy was fulfilled against and lets play continue.
// ─────────────────────────────────────────────────────────────

import { updatePlayer as updatePlayerRaw, updateMatch, updateTeamAnima, getMatch } from "./gameManager";
// R97 · THE PROPHET'S TOLL and R98 · CO-WINNERS both live as pure
// arithmetic in rules.js so the engine, the Debugger and the harness
// cannot each hold their own copy of the ruling.
import { isOneVsOne, prophetsToll, ANIMA_LOSS_KINDS, animaLossFeedsProphecy } from "./rules";
import { finishOrEliminateSeat, setCoWinnerResolver } from "./matchOutcome";
import { allSeatIdxs, getTargetIdx, isEliminated, teamOf, livingEnemySeatsOf,
         sharesAnimaWithTeam, getEffectiveAnima } from "./matchSeats";
import { ESCENDANTS, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, SUPREME_TITLES } from "./cards";
import { baseId } from "./necroSeal";  // see the note on getCard below

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

// ⭐ SEAL-AWARE, LIKE EVERY OTHER CARD LOOKUP IN THE GAME.
//
// A being that has been Resurrected or has come back through a
// Graveshift wears a NECRO SEAL on its id — `taez_lord` becomes
// `taez_lord~1~7` — which is the whole point of necroSeal.js: two
// ruined copies of one card are two different beings and a flat array
// of identical strings cannot say so.
//
// This lookup compared the sealed string directly, so it returned null
// the moment a being came back. isSupreme then returned false, and
// Space Race, Phantom Planet, Dragonforce, Time to Pretend, Tablet of
// O'mnis and Tablet of N'ullus all stopped auto-firing FOREVER, while
// the board went on showing an intact Supreme Being.
//
// checkTabletNullus is the ugly one: it watches the ADVERSARY's field,
// so the OPPONENT'S Graveshift silently disabled your win condition and
// nothing on either screen said a word.
//
// It hid because the inline twin inside ProphecyButton already uses the
// seal-aware getCard — so the manual activation button kept working and
// casual testing never noticed the automatic one had stopped.
const getCard = (id) => {
  if (!id) return null;
  const bare = baseId(id);
  return [...ESCENDANTS, ...IMPERA, ...UHLTIS, ...AFFINITIES].find(c => c.id === bare) || null;
};

// Check if a slot contains a Supreme Being:
// Natural L3 Escendant + soul has 3 Impera (L1,L2,L3) + 1 U'hlti + 1 Affinity
function isSupreme(slot) {
  if (!slot?.escendant || slot.fallen || !slot.natural) return false;
  const card = getCard(slot.escendant);
  if (!card || card.level !== 3) return false;

  const soul = slot.soul || [];
  const soulCards = soul.map(id => getCard(id)).filter(Boolean);

  const impera = soulCards.filter(c => c.type === "Impera");
  const hasL1Impera = impera.some(c => c.level === 1);
  const hasL2Impera = impera.some(c => c.level === 2);
  const hasL3Impera = impera.some(c => c.level === 3);
  const hasUhlti = soulCards.some(c => c.type === "U'hlti");
  const hasAffinity = soulCards.some(c => c.type === "Affinity");

  return hasL1Impera && hasL2Impera && hasL3Impera && hasUhlti && hasAffinity;
}

// Get affinity title of a Supreme Being slot
// Evil = A'oeternis, Holy = A'aeternis, Enlightened = V'igiluz, Jester = J'ynxi
function getSupremeTitle(slot) {
  const soul = slot.soul || [];
  const soulCards = soul.map(id => getCard(id)).filter(Boolean);
  const affinity = soulCards.find(c => c.type === "Affinity");
  if (!affinity) return null;
  const id = affinity.id;
  if (id === "aff_evil") return "A'oeternis";
  if (id === "aff_holy") return "A'aeternis";
  // ⚠ THE IDS WERE WRONG AND SO WAS ONE OF THE STRINGS. cards.js ships
  // aff_holy / aff_awoken / aff_evil / aff_jynxi — there is no
  // "aff_enlightened" and no "aff_jester" anywhere in the game, so these
  // two branches were unreachable. Nothing mis-fires today because all
  // eight Prophecies ask for A'oeternis or A'aeternis, but getSupremeTitle
  // is a contract and half of it was dead. The Jester's title is also
  // "Jester" (SUPREME_TITLES.JYNXI); "J'ynxi" is its Ilk.
  if (id === "aff_awoken") return SUPREME_TITLES.AWOKEN;
  if (id === "aff_jynxi") return SUPREME_TITLES.JYNXI;
  return null;
}

// Get the SCHOOL of a slot's escendant.
// ⚠ RENAMED 2026-08-25 (getSlotIlk -> getSlotSchool). The four
// "ilk Prophecies" — Space Race, Phantom Planet, Dragonforce, Time to
// Pretend — key on Space/Phantom/Dragon/Time, which the designer's
// ruling of 2026-08-25 fixes as the SCHOOL axis, not the Ilk axis.
// cards.js used to store those words in `card.ilk`; it now stores them
// in `card.school`, so this reads `.school`. The Prophecies' player-
// facing names are UNCHANGED — only the field they read.
function getSlotSchool(slot) {
  if (!slot?.escendant) return null;
  return getCard(slot.escendant)?.school || null;
}

// Get all active slots (not fallen)
function activeSlots(field) {
  return (field?.esclings || []).filter(s => s?.escendant && !s.fallen);
}

// ─────────────────────────────────────────────────────────────
// INDIVIDUAL PROPHECY CHECKERS
// Each returns true if conditions are met
// ─────────────────────────────────────────────────────────────

// Space Race / Phantom Planet / Dragonforce / Time to Pretend
// 2 Supreme Beings of a School + 1 A'oeternis or A'aeternis of that School
// ⚠ RENAMED 2026-08-25 (checkIlkProphecy -> checkSchoolProphecy) — see
// getSlotSchool above. Same four Prophecies, same names on the cards,
// same beings identified; the axis is now called what it is.
function checkSchoolProphecy(myField, cls) {
  const slots = activeSlots(myField);
  const supremeOfClass = slots.filter(s => isSupreme(s) && getSlotSchool(s) === cls);
  if (supremeOfClass.length < 2) return false; // need at least 2 Supreme
  // Need at least 1 A'oeternis or A'aeternis of that School
  const hasDivineTitle = supremeOfClass.some(s => {
    const title = getSupremeTitle(s);
    return title === "A'oeternis" || title === "A'aeternis";
  });
  return hasDivineTitle;
}

// Tablet of O'mnis: 2 Supreme Beings (any School) + 1 A'oeternis or A'aeternis (any School)
function checkTabletOmnis(myField) {
  const slots = activeSlots(myField);
  const supremes = slots.filter(s => isSupreme(s));
  if (supremes.length < 2) return false;
  return supremes.some(s => {
    const title = getSupremeTitle(s);
    return title === "A'oeternis" || title === "A'aeternis";
  });
}

// Tablet of N'ullus: Opponent has 2 Supreme Beings + 1 A'oeternis or A'aeternis
function checkTabletNullus(oppField) {
  return checkTabletOmnis(oppField); // same condition, checked on opponent's field
}

// Bloody Hell: a Warlock (either side) has lost 45+ Anima total across
// the duel — TO AN ADVERSARY. R100 (caP'n, 2026-08-26): *"dimming your
// own Anima in any way at all does not satisfy Prophecy requirements."*
// `totalAnimaLost` is the adversarial ledger and nothing else writes to
// it; the self-dim ledger is `totalAnimaSelfDimmed` and this predicate
// must never learn its name.
function checkBloodyHell(mePlayer, oppPlayer) {
  return (mePlayer?.totalAnimaLost || 0) >= 45 || (oppPlayer?.totalAnimaLost || 0) >= 45;
}

// ─────────────────────────────────────────────────────────────
// ⚰ THE GHOST PROPHECIES, BURIED (caP'n ruling, 2026-08-25)
// ─────────────────────────────────────────────────────────────
// checkZeroHour, checkPlateOfGreed and checkTrexKhao lived here for
// months behind a long banner explaining that they were "three
// finished win conditions waiting on a designer's yes". They were on
// no card in src/cards.js, in no `case` of the switch below, and
// reachable from nothing.
//
// caP'n, 2026-08-25: *"they're not real cards I made a bunch of extra
// cards and slimmed it those didn't make the final run."*
//
// So they are deleted rather than kept as documentation of a decision
// that was never going to be made. The answer was no.
//
// ⚠ WHAT IS *NOT* DELETED, AND WHY THIS NOTE EXISTS. All three read the
// PER-HALF-TURN Anima ledger (animaGainedThisTurn / animaGainedLastTurn
// / animaLostThisTurn), and buildEndOfHalfTurnUpdate at the bottom of
// this file is what rotates it. That rotation IS LOAD-BEARING and stays:
// p_dg0_3 (DG-0 I'nfini — "draw 1 if you gained Anima this turn") reads
// `animaGainedThisTurn` and is a real, shipping card. Deleting the
// rotation along with its ghost callers would have made that Impera
// permanently true after its first Anima gain, which is the exact bug
// src/turnEngine.js was written to fix. The ledger has a live consumer;
// only these three checkers did not.
// ─────────────────────────────────────────────────────────────

// Ace of Shades: 7 Shadow Gamblings won in a row
function checkAceOfShades(myPlayer) {
  return (myPlayer?.shadowGamblingStreak || 0) >= 7;
}


// ─────────────────────────────────────────────────────────────
// SCHOOLS (match cards.js `card.school` values — Family B)
// ─────────────────────────────────────────────────────────────
const SCHOOL_SPACE   = "Space";
const SCHOOL_PHANTOM = "Phantom";
const SCHOOL_DRAGON  = "Dragon";
const SCHOOL_TIME    = "Time";

// ─────────────────────────────────────────────────────────────
// MAIN CHECKER — asked at every half-turn hand-off
// ─────────────────────────────────────────────────────────────
// WHERE THIS IS CALLED FROM, and why it moved (August 2026):
// src/turnEngine.js · handOffHalfTurn, which is the ONE place a
// half-turn changes hands in either driver. Until then this had a
// single call site inside Shatterfield.js's onValue listener — a win
// condition living in the renderer, invisible to every headless driver.
// See turnEngine.js's banner for the full account, including why R33
// now wins the race against a Prophecy completed mid-turn.
//
// RETURNS a result rather than nothing, so the caller can say what
// happened without re-reading the match:
//   null                                    — nobody's Prophecy fired
//   { seat, prophecyId, cost, ended, ... }  — it did; `ended` says
//                                             whether the whole match
//                                             is over or one side was
//                                             merely knocked out.
// ─────────────────────────────────────────────────────────────
// THE CONDITION, AS A PURE FUNCTION
// ─────────────────────────────────────────────────────────────
// Lifted verbatim out of checkProphecies' switch on 2026-08-25 for
// R98 (co-winners), which has to ask "is this seat's Prophecy true?"
// about a match that is ALREADY FINISHED — a question the resolving
// loop cannot answer, because the resolving loop charges Khao, writes
// a spend stamp and ends duels. One card, one condition, no side
// effects, asked from both places.
export function prophecyConditionMet(prophecyId, mePlayer, oppPlayer) {
  const myField = mePlayer?.field;
  const oppField = oppPlayer?.field;
  switch (prophecyId) {
    case "proph_space_race":       return checkSchoolProphecy(myField, SCHOOL_SPACE);
    case "proph_phantom_planet":   return checkSchoolProphecy(myField, SCHOOL_PHANTOM);
    case "proph_dragonforce":      return checkSchoolProphecy(myField, SCHOOL_DRAGON);
    case "proph_time_to_pretend":  return checkSchoolProphecy(myField, SCHOOL_TIME);
    case "proph_tablet_omnis":     return checkTabletOmnis(myField);
    case "proph_tablet_nullus":    return checkTabletNullus(oppField);
    case "proph_bloody_hell":      return checkBloodyHell(mePlayer, oppPlayer);
    case "proph_ace_of_shades":    return checkAceOfShades(mePlayer);
    default:                       return false;
  }
}

// ═══════════════════════════════════════════════════════════════
// 🏆 R98 · PROPHECIES GRANT WINS, THEY DO NOT PREVENT THEM
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-25, verbatim:
//
//   *"that is sort of the idea for prophecies where you might actually
//   want someone to deliver the finishing blow thereby fulfilling your
//   prophecy's condition and you also win, keyword, also, both players
//   win; the first player still managed to ruin the first Aenarchist,
//   prophecies do not prevent wins merely grant them, the other player
//   can still win naturally or by their own prophecy"*
//
// ⚠ THIS OVERTURNS A RULING WRITTEN IN turnEngine.js's BANNER, and the
// overturn is the point. That banner said "R33 WINS THE RACE... a
// Solrend already won on Anima is over" and asked nobody to move the
// Prophecy check back into a listener. caP'n's ruling says the exact
// opposite: the finishing blow is precisely the moment a Prophecy is
// MEANT to come true. R33 still decides who the PRIMARY winner is —
// that has not changed, and `match.winner` still names them. What has
// changed is that a Prophecy true at that instant now ALSO wins,
// instead of being silently discarded because somebody else got there
// first.
//
// This function is the pure half: given the board as it stood when the
// duel ended, which OTHER seats were sitting on a fulfilled, unspent,
// affordable Prophecy? It writes nothing — matchOutcome does the
// writing, because matchOutcome is the one place that knows a Solrend
// has ended (see THE CO-WINNER RESOLVER, INJECTED, over there).
// ⚠ A SEAT THAT WAS JUST KNOCKED OUT CAN CO-WIN, AND THAT IS THE WHOLE
// RULING. The first cut of this function was asked about the PROJECTED
// board — the one with the losing side already flagged out — and the
// caller then filtered those seats away. In 1v1 that made R98
// unreachable: there are only two players, one of them delivered the
// finishing blow, and the other one is the seat that just took it. Read
// caP'n's sentence again — *"you might actually want someone to deliver
// the finishing blow thereby fulfilling your prophecy's condition and
// you also win, keyword, also, BOTH PLAYERS WIN"*. "Both players" in a
// duel is the winner and the seat that just lost. A rule that only ever
// fires in a 3-4p FFA cannot be what that sentence describes.
//
// So this is asked about the board AS THE CALLER HELD IT — before the
// projection — and the only seats it refuses are ones that were ALREADY
// out before this blow. Leaving the table at turn 20 does not entitle
// you to a win at turn 90; being killed by the very blow that completed
// your Prophecy does.
export function coWinningSeats(match, primaryWinner) {
  if (!match) return [];
  const players = match.players || {};
  const out = [];
  for (const idx of allSeatIdxs(match)) {
    const p = players[idx];
    if (!p) continue;
    // Already out BEFORE this blow — not a co-winner, just gone.
    if (p.eliminated) continue;
    // The seat that won on the board is already the winner; a Prophecy
    // it also happened to fulfil adds nothing and must not double-file.
    if (idx === primaryWinner) continue;
    // A TEAMMATE of the primary winner has already won — same reason.
    const t = teamOf(match, idx);
    if (t != null && t === primaryWinner) continue;
    const prophecyId = p.prophecy;
    if (!prophecyId) continue;
    // R99 — one Prophecy, one resolution. A spent card grants nothing.
    if (p.prophecySpent === prophecyId) continue;
    // The fare is still the fare. A Prophecy you cannot pay for does
    // not fire on the automatic path and does not co-win on this one.
    if ((p.khao || 0) < getProphecyKhaoCost(prophecyId)) continue;
    const oppIdx = getTargetIdx(match, idx);
    const oppPlayer = oppIdx == null ? null : players[oppIdx];
    if (!oppPlayer) continue;
    if (prophecyConditionMet(prophecyId, p, oppPlayer)) {
      out.push({ seat: idx, prophecyId, cost: getProphecyKhaoCost(prophecyId), id: t == null ? idx : t });
    }
  }
  return out;
}

// ════════════════════════════════════════════════════════════════════
// 🩸 R97 · THE PROPHET'S TOLL — ONE IMPLEMENTATION, TWO DOORS
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-08-25, verbatim:
//
//   *"any game mode that isn't 1v1: if a prophecy is fulfilled, dim all
//   of your adversary's Anima by 15, if their Anima is currently
//   greater than 30, halve it, round up."*
//
// ⚠ THE RULING THIS FUNCTION EXISTS TO WRITE DOWN, stated plainly
// because it is a reading and not a repair:
//
//   ▸ A POOLED TEAM IS ONE ADVERSARY FOR THE TOLL.
//
// In a shared-Anima 2v2 (caP'n: "they share Anima and not Khao") two
// seats have ONE life total at `teamAnima/{t}`. "All of your
// adversary's Anima" is that ONE number. Tolling it once per enemy SEAT
// would charge a 2v2 team double what a 3-4p FFA seat pays for the same
// Prophecy — 40 → 20 → 5 instead of 40 → 20 — which is the opposite of
// the reason multiplayer got its own clause at all. So the adversaries
// are the SIDES, not the chairs: seats that pool are folded into one
// row, tolled once, and the loss ledger is incremented once, on the
// seat that stands for the side.
//
// ⚠ AND WHY IT IS A FUNCTION RATHER THAN TWO LOOPS. It was two loops.
// prophecyEngine's read the RAW per-seat `players/{i}/anima` and wrote
// it back through updatePlayerRaw; Shatterfield's manual button read
// `getEffectiveAnima` and wrote through the renderer's updatePlayer
// shadow, which redirects `anima` to `teamAnima`. In shared-Anima 2v2
// that is not a discrepancy in emphasis — the engine's toll landed on a
// field that `getEffectiveAnima` never reads, so R97 was a COMPLETE
// NO-OP on the automatic door while the manual one worked. The banner
// over the manual door said "BOTH DOORS OR NEITHER … exactly the same
// shape" the whole time. It is now literally the same code.
//
// ⚠ THIS WRITES THROUGH gameManager, NOT THROUGH SHATTERFIELD'S SHADOW,
// on BOTH doors — the pooled redirect is done here, explicitly, so an
// engine file and a React file produce byte-identical writes. See
// turnEngine.js's banner on why an engine file must never reach for the
// renderer's local updatePlayer.

// Who the toll is charged to, as SIDES. One row per adversary: a
// pooled team collapses to a single row whose `seats` lists the chairs
// it covers and whose `seat` is the one that carries the ledger.
export function prophetsTollTargets(match, myIdx) {
  const rows = [];
  const bySide = new Map();
  for (const idx of livingEnemySeatsOf(match, myIdx)) {
    if (!match?.players?.[idx]) continue;
    const pooled = sharesAnimaWithTeam(match, idx);
    const t = teamOf(match, idx);
    const key = pooled ? `team:${t}` : `seat:${idx}`;
    const already = bySide.get(key);
    if (already) { already.seats.push(idx); continue; }
    // EFFECTIVE Anima, always — the pooled total for a team, the seat's
    // own number for everyone else. This is the read the manual door
    // always did and the automatic door never did.
    const row = { key, seat: idx, seats: [idx], team: pooled ? t : null, pooled, ...prophetsToll(getEffectiveAnima(match, idx)) };
    bySide.set(key, row);
    rows.push(row);
  }
  return rows;
}

// ── R08 · DECLARED, AND THE DECLARATION IS A RULING ─────────────────
// gameManager's seal refuses an adversary-side Anima dim during the
// opening Cycle. A Prophet's Toll IS such a dim, and it is NOT an
// Attack: it is a Prophecy resolving — the win condition the seat has
// been holding since deck-build, arriving. R08 is about Attacking; R97
// is about a Prophecy coming true.
//
// It is unreachable in practice — Ace of Shades wants seven Shadow
// Gamblings won in a row and a 13-Khao fare, Bloody Hell wants 45 Anima
// lost, and neither is available on half-turn 0 or 1 — but a silent
// refusal would eat a WIN CONDITION rather than an attack, and that is
// the wrong way to be wrong. Declared, so it is a decision on the
// record instead of an accident waiting on a faster Prophecy.
const TOLL_IS_NOT_AN_ATTACK = { notAnAttack: "R97 · the Prophet's Toll is a Prophecy resolving, not an Attack" };

// Charge it. Returns the rows actually applied (a side already on 0
// dims by 0 and is skipped, exactly as both loops did).
export async function applyProphetsToll(matchCode, match, myIdx) {
  const rows = prophetsTollTargets(match, myIdx).filter((r) => r.dimmed > 0);
  for (const r of rows) {
    const ledgerSeat = match?.players?.[r.seat] || null;
    if (r.pooled) {
      // The life total lives at teamAnima/{t}; the per-seat `anima`
      // field is not written because nothing reads it under
      // sharedAnimaTeams and a stale copy of a life total is worse
      // than no copy. The loss ledger is per-seat and is written ONCE,
      // on the side's representative chair — the same shape every
      // ordinary pooled hit in Shatterfield already produces.
      await updateTeamAnima(matchCode, r.team, r.to, TOLL_IS_NOT_AN_ATTACK);
      await updatePlayerRaw(matchCode, r.seat, buildAnimaLossUpdate(ledgerSeat, r.dimmed));
    } else {
      await updatePlayerRaw(matchCode, r.seat, { anima: r.to, ...buildAnimaLossUpdate(ledgerSeat, r.dimmed) }, TOLL_IS_NOT_AN_ATTACK);
    }
    // eslint-disable-next-line no-console
    console.log(`[R97] the Prophet's Toll — ${r.pooled ? `team ${r.team} (seats ${r.seats.join("+")})` : `seat ${r.seat}`} ${r.from} → ${r.to}${r.halved ? " (halved, round up)" : " (-15)"}`);
  }
  return rows;
}

// The board AS THE TOLL LEFT IT. finishOrEliminateSeat decides whether
// the Solrend is over by asking `matchIsDecided` about the snapshot it
// is handed — so handing it the PRE-toll match asks whether the table
// was decided before anybody was dimmed, which is never the question.
export function boardAfterToll(match, rows) {
  if (!rows || !rows.length) return match;
  const players = { ...(match.players || {}) };
  const teamAnima = { ...(match.teamAnima || {}) };
  for (const r of rows) {
    const before = players[r.seat] || {};
    players[r.seat] = { ...before, ...buildAnimaLossUpdate(before, r.dimmed) };
    if (r.pooled) teamAnima[r.team] = r.to;
    else players[r.seat] = { ...players[r.seat], anima: r.to };
  }
  return { ...match, players, teamAnima };
}

// How the archive narrates it. A side, not a chair: `1:44->22` for a
// seat, `team B:40->20` for a pooled team.
export const tollStamp = (rows) =>
  (rows || []).map((r) => `${r.pooled ? `team ${r.team}` : r.seat}:${r.from}->${r.to}`).join(",");

// ── R33 COLLECTS ON EVERY SIDE THE TOLL EMPTIED, NOT THE FIRST ──────
// This used to be `finishOrEliminateSeat(code, match, myIdx,
// emptied[0].seat)` — one call, the PRE-toll board — so a Prophecy that
// took the last two opponents in a 3-seat FFA to 0 flagged ONE of them
// out, left `status: active`, and handed the chair back to the winner,
// who then sat alone until R93's clock rang. A Prophecy that killed the
// whole table was archived as a CLOCK CALL.
//
// So: the tolled board goes in, every emptied side is collected, and
// the loop stops the moment the resolver says the Solrend ended. The
// live board is re-read between collections because the FIRST one may
// have moved the chair (finishOrEliminateSeat hands off when the seat
// it removes was holding it) and a stale `activePlayer` would seat the
// next knockout's hand-off on a seat that has already left.
export async function collectEmptiedSides(matchCode, tolledBoard, myIdx, emptied) {
  let board = tolledBoard;
  let outcome = { ended: false, winner: null, winners: [], runnerUp: null, eliminatedSeats: [] };
  for (let n = 0; n < emptied.length; n++) {
    const row = emptied[n];
    const res = await finishOrEliminateSeat(matchCode, board, myIdx, row.seat);
    const seats = [...outcome.eliminatedSeats, ...(res.eliminatedSeats || [])]
      .filter((v, i, a) => a.indexOf(v) === i);
    outcome = { ...res, eliminatedSeats: seats };
    if (res.ended) {
      // The Solrend is over, so no further elimination FLAG is written
      // — but the sides this toll also emptied are out, by Anima, and a
      // caller narrating "who left the table" is entitled to hear about
      // all of them rather than only the one the resolver happened to
      // be handed first.
      for (let k = n + 1; k < emptied.length; k++) {
        emptied[k].seats.forEach((i) => { if (!outcome.eliminatedSeats.includes(i)) outcome.eliminatedSeats.push(i); });
      }
      break;
    }
    let live = null;
    try { live = await getMatch(matchCode); } catch (e) { live = null; }
    if (live && live.players) board = live;
    else {
      // FALLBACK — no store to re-read (a caller driving a detached
      // snapshot). Flag the knocked-out seats locally so the next
      // collection at least does not decide against a seat it removed.
      const players = { ...(board.players || {}) };
      (res.eliminatedSeats || []).forEach((i) => { players[i] = { ...players[i], eliminated: true }; });
      board = { ...board, players };
    }
    if (board.status === "finished") { outcome = { ...outcome, ended: true, winner: board.winner ?? outcome.winner }; break; }
  }
  return outcome;
}

export async function checkProphecies(matchCode, match) {
  if (!match || match.status === "finished") return null;

  const players = match.players || {};
  // N-SEAT. On a classic two-seat match this is `[0, 1]` and every line
  // below behaves exactly as the hardcoded p0/p1 pair it replaced —
  // getTargetIdx returns the one other seat whenever seatCount <= 2.
  // A seat already knocked out of a 3-4p FFA no longer gets to win.
  //
  // ⚠ LIVENESS IS `matchSeats.isEliminated`, NOT THE RAW FLAG, AND THAT
  // IS A BUG FIX WITH A NAME. This line used to read
  // `!players[i].eliminated`, which was the ONLY liveness test in this
  // file and the only one in the project that did not also ask about
  // Anima. Everywhere else — livingSeats, getTargetIdx,
  // survivingTeamsOrSeats, matchIsDecided — goes through isEliminated,
  // which is `p.eliminated || getEffectiveAnima(match, idx) <= 0`.
  // So a Prophecy never looked at Anima, and A SEAT ON 0 ANIMA WON THE
  // SOLREND. It was reachable two ways and neither is exotic:
  //   (a) the async gap. R33 ends a duel at ~11 sites and every one of
  //       them zeroes Anima and calls endMatch in two separate awaits;
  //       handOffHalfTurn interleaves its own round-trips into exactly
  //       that window.
  //   (b) R95. Boss phases SUSPEND R33 for the boss seat between
  //       phases, so a boss sits on 0 Anima with the match legitimately
  //       active — and an AI seat can carry a `prophecy` field.
  // Worse, matchOutcome's matchIsDecided uses the REAL isEliminated, so
  // with the corpse firing the Prophecy `survivors` came back EMPTY and
  // the duel was awarded to the dead seat by the fallback. Sealed here,
  // at the source, because this is the one place that disagreed.
  //
  // A knock-on that is intended, not incidental: during an R95 phase
  // window the boss seat is not "living", so seats.length drops below 2
  // and NO Prophecy fires at all until the phase write lands. A duel
  // mid-escalation is not a duel anybody wins by Prophecy.
  const seats = allSeatIdxs(match).filter((i) => players[i] && !isEliminated(match, i));
  if (seats.length < 2) return null;

  for (const myIdx of seats) {
    const mePlayer = players[myIdx];
    const oppIdx = getTargetIdx(match, myIdx);
    const oppPlayer = oppIdx == null ? null : players[oppIdx];
    if (!oppPlayer) continue;

    const prophecyId = mePlayer?.prophecy;
    if (!prophecyId) continue;

    // ── A PROPHECY IS SPENT WHEN IT RESOLVES ─────────────────────
    // ⚠ THIS IS A RULING, NOT A REPAIR, AND IT IS MINE (the August
    // 2026 coherency pass). No rule row said it, so this file says it:
    //
    //     A PROPHECY IS SPENT WHEN IT RESOLVES. One card, one
    //     fulfilment, one Solrend.
    //
    // WHAT IT FIXES, measured. This loop had no idempotence guard of
    // any kind, and the condition it asks is re-evaluated against an
    // UNCHANGED BOARD at every single hand-off — `shadowGamblingStreak`
    // is still 9, the two Supreme Beings are still standing. In a 1v1
    // that was invisible because the first fire ended the duel. In a
    // 3-4p FFA it is not: the first fire only KNOCKS OUT one seat, so
    // the same Prophecy came true again on the next hand-off, and
    // again, until one card had eaten the whole table by itself:
    //
    //     round 0: prophecyEnded=true  ended=false  elim=[1]    khao 86
    //     round 1: prophecyEnded=true  ended=false  elim=[1,2]  khao 73
    //     round 2: prophecyEnded=true  ended=true   elim=[1,2]  khao 60
    //
    // The fare is no brake — Khao refills to maxKhao at every Turn
    // start, so 13 a turn is free money — and the match-level stamps
    // were overwritten each time, so the archive could not even tell
    // you it had happened three times. The manual button and this path
    // could also ALTERNATE: handleActivateProphecy knocked a seat out
    // without stamping anything, and the next hand-off fired the same
    // Prophecy automatically and charged for it a second time.
    //
    // THE SAFE READING, chosen deliberately. The alternative reading —
    // "a Prophecy is a standing condition and keeps winning" — is a
    // card that beats a four-seat table alone for one Khao payment a
    // turn. If a designer ever wants that, they want it stated out
    // loud, not inherited from a missing `if`. So: spent on resolve.
    // `prophecySpent` is written on the SEAT (per-copy, per-Solrend)
    // and both doors check it — see Shatterfield's
    // handleActivateProphecy for the manual twin.
    if (mePlayer.prophecySpent === prophecyId) continue;

    const myKhao = mePlayer?.khao || 0;
    const myField = mePlayer?.field;
    const oppField = oppPlayer?.field;

    // Get Prophecy Khao cost (most are 15, some differ)
    const propKhaoCost = getProphecyKhaoCost(prophecyId);
    if (myKhao < propKhaoCost) continue; // not enough Khao

    const conditionMet = prophecyConditionMet(prophecyId, mePlayer, oppPlayer);
    let prophetTollApplied = null;   // R97, filled in below outside 1v1

    if (conditionMet) {
      // "resolves", not "wins" — in a 3-4p FFA this may only be
      // knocking one seat out. See THE STAMPS below.
      console.log(`[Prophecy] ${mePlayer.name} resolves ${prophecyId}!`);

      // ── THE FARE, WHICH THIS PATH USED TO GET FOR FREE ───────────
      // ⚠ BEHAVIOUR CHANGE, August 2026, and it is coherency rather
      // than a new rule. The gate above has always been `myKhao <
      // propKhaoCost → continue`, i.e. the Khao was REQUIRED — but
      // nothing here ever spent it. Shatterfield's manual
      // handleActivateProphecy calls spendKhao(cost) and always has.
      // Two doors to the same win, and one of them let you keep your
      // fifteen Khao. Now both charge. Written before the match ends
      // because a Solrend's final archived state should show the fare
      // that bought it.
      //
      // Raw updatePlayer, and no `anima` key in the payload — see
      // turnEngine.js's banner on why an engine file must never go
      // through Shatterfield's local updatePlayer shadow.
      //
      // ONE write, always — the fare AND the spend stamp together. It
      // is unconditional even at cost 0 because the stamp is what makes
      // the ruling above enforceable, and a free Prophecy that never
      // marks itself spent is the same bug wearing a discount.
      await updatePlayerRaw(matchCode, myIdx, {
        khao: propKhaoCost > 0 ? Math.max(0, myKhao - propKhaoCost) : myKhao,
        prophecySpent: prophecyId,
      });

      // ── N-SEAT: KNOCK OUT, DON'T END THE TABLE ───────────────────
      // This used to call gameManager's raw endMatch, which sets
      // `status: "finished"` on the whole match. In a 3-4p FFA or a 2v2
      // that ended the game for seats that were never involved, while
      // the MANUAL prophecy button (which goes through the renderer's
      // finishOrEliminate) correctly knocked out one side. Same
      // resolver on both doors now.
      // ═══════════════════════════════════════════════════════════
      // 🩸 R97 · THE PROPHET'S TOLL — non-1v1 does not eliminate
      // ═══════════════════════════════════════════════════════════
      // caP'n, 2026-08-25, verbatim:
      //
      //   *"any game mode that isn't 1v1: if a prophecy is fulfilled,
      //   dim all of your adversary's Anima by 15, if their Anima is
      //   currently greater than 30, halve it, round up."*
      //
      // ⚠ THE READING, FLAGGED FOR VETO. "all of your adversary's
      // Anima" is read as EVERY living adversary, not one — outside
      // 1v1 there is more than one adversary, and a Prophecy that only
      // hit one of them would be WEAKER in a four-player game than in a
      // duel, which inverts the whole reason multiplayer got its own
      // clause. The two dims are exclusive and in the designer's own
      // order: over 30 → halve (round up), else → flat 15. prophetsToll
      // in rules.js is the one implementation.
      //
      // It is an ADVERSARY EFFECT, not a voluntary payment, so R96 (THE
      // TILL LAW) does not protect against it — R96 governs what you do
      // to YOURSELF — and R33 collects normally if the toll lands a
      // seat on 0. That is why the toll is applied and THEN
      // finishOrEliminateSeat is asked about any seat it emptied,
      // rather than the toll being some special kind of damage.
      let outcome;
      if (!isOneVsOne(match)) {
        // ONE implementation, shared with Shatterfield's manual button
        // — see the banner over applyProphetsToll. A pooled team is one
        // adversary; the write goes wherever that side's life actually
        // lives; the ledger is incremented once per SIDE.
        const tolled = await applyProphetsToll(matchCode, match, myIdx);
        // R33 collects on EVERY side the toll emptied, against the
        // board AS THE TOLL LEFT IT — see collectEmptiedSides.
        const emptied = tolled.filter((t) => t.to <= 0);
        outcome = emptied.length
          ? await collectEmptiedSides(matchCode, boardAfterToll(match, tolled), myIdx, emptied)
          : { ended: false, winner: null, runnerUp: null, eliminatedSeats: [] };
        prophetTollApplied = tolled;
      } else {
        // ── 1v1: THE SOLREND IS WON OUTRIGHT (subject to R98) ──────
        // This used to call gameManager's raw endMatch, which sets
        // `status: "finished"` on the whole match. In a 3-4p FFA or a
        // 2v2 that ended the game for seats that were never involved,
        // while the MANUAL prophecy button (which goes through the
        // renderer's finishOrEliminate) correctly knocked out one side.
        // Same resolver on both doors now — and outside 1v1 the branch
        // above means the resolver is only reached when the toll
        // actually killed somebody.
        outcome = await finishOrEliminateSeat(matchCode, match, myIdx, oppIdx);
      }

      // ── THE STAMPS, AND WHY THERE ARE NOW TWO OF THEM ────────────
      // `wonByProphecy` is to R63 what `calledOnClock` is to R93: the
      // engine's own answer to "what ended this duel", written into the
      // archive instead of reconstructed afterwards by whoever reads it.
      //
      // ⚠ IT USED TO BE WRITTEN THE INSTANT A PROPHECY FIRED, INCLUDING
      // THE N-SEAT CASE WHERE THE MATCH GOES ON. So a Prophecy that
      // merely knocked seat 1 out on turn 5 left `wonByProphecy`
      // standing on a duel that then ran another 145 half-turns and was
      // CALLED ON THE CLOCK — the engine wrote `calledOnClock: "R93"`
      // and the balancer, which tested the Prophecy stamp first, filed
      // the duel as a Prophecy win. The stamp outlived its own event.
      //
      // Two facts, two fields, and the names now say which is which:
      //   prophecyResolved / prophecyResolvedBy — a Prophecy CAME TRUE
      //     here. True of a knockout in a 3-4p FFA. Never a claim about
      //     how the duel ended.
      //   wonByProphecy / prophecyWinner       — a Prophecy ENDED THE
      //     SOLREND. Only ever written alongside `status: finished`.
      // In 1v1 they are the same instant, which is exactly why they had
      // to be two fields rather than one.
      //
      // WRITTEN AFTER the resolver, not before, because only the
      // resolver knows which of the two happened. The archive's final
      // state carries both; a listener can see `finished` for one
      // round-trip before `wonByProphecy` lands, which is the same
      // ordering every other end-of-duel telemetry in the game has.
      const stamp = { prophecyResolved: prophecyId, prophecyResolvedBy: myIdx };
      if (outcome.ended) { stamp.wonByProphecy = prophecyId; stamp.prophecyWinner = myIdx; }
      // R97 — outside 1v1 the ordinary outcome is a TOLL and no ending
      // at all, so the archive records that a Prophecy came true and
      // what it cost the table, and pointedly does not claim a win.
      if (prophetTollApplied && prophetTollApplied.length) {
        stamp.prophetsToll = tollStamp(prophetTollApplied);
      }
      await updateMatch(matchCode, stamp);

      return {
        seat: myIdx, prophecyId, cost: propKhaoCost, against: oppIdx,
        ended: outcome.ended, winner: outcome.winner,
        winners: outcome.winners || (outcome.winner != null ? [outcome.winner] : []),
        eliminatedSeats: outcome.eliminatedSeats,
        // R97 — null in 1v1, an array of { seat, from, to, dimmed,
        // halved } everywhere else, so a caller can narrate the toll
        // without re-reading the board to work out what it did.
        toll: prophetTollApplied,
      };
    }
  }
  return null;
}

// ── THE FARE, FROM THE CARD FACE ────────────────────────────────────
// ⚠ THIS NUMBER WAS DECLARED IN FOUR PLACES and they only happened to
// agree: src/cards.js `khaoCost` (the number PRINTED ON THE CARD, read
// by CardRenderer and BoosterPack), this function (the number actually
// CHARGED on the automatic path), Shatterfield's handleActivateProphecy
// (charged on the manual path) and Shatterfield's ProphecyButton (which
// gates the button and tells the player what it will cost). Three of
// those four were hand-typed `{ proph_ace_of_shades: 13 } ?? 15` maps.
// A card whose printed price and charged price can disagree is a bug
// nobody would ever think to look for, and charging the fare on BOTH
// doors (August 2026) is what made it load-bearing rather than cosmetic.
//
// The card is the natural home, so the card is the home. This is now
// the ONLY derivation in the project and the other three read it.
// src/cards.js is unchanged — every PROPHECIES row already carries a
// `khaoCost`, and all eight already agreed with the map this replaced.
//
// The 15 is still a default rather than a throw: an id with no row —
// a card mid-authoring, a save from an older build — should cost the
// standard fare rather than crash the hand-off. (It used to say "the
// three unwired win conditions above"; those were deleted on
// 2026-08-25, see THE GHOST PROPHECIES, BURIED.)
// ── R98 · REGISTERING THE CO-WINNER RESOLVER ────────────────────────
// Done on load, at the bottom, exactly the way turnEngine registers its
// hand-off with this same module. This file already sits ABOVE
// matchOutcome in the dependency graph, so registering costs nothing
// and adds no cycle — and matchOutcome keeps working with no
// co-winners at all for any consumer that never loads this file.
setCoWinnerResolver(coWinningSeats);

export function getProphecyKhaoCost(id) {
  const card = PROPHECIES.find((c) => c.id === id);
  const printed = card == null ? null : Number(card.khaoCost);
  return Number.isFinite(printed) ? printed : 15;
}

// ─────────────────────────────────────────────────────────────
// ANIMA TRACKING HELPERS
// Call these from Shatterfield whenever Anima changes
// ─────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════
// 🩸 R100 · THE LEDGER IS TWO LEDGERS
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"Also I don't know if you asked later or if I ever mentioned it,
//   but dimming your own Anima in any way at all does not satisfy
//   Prophecy requirements."*
//
// This function used to have one job and two callers' worth of
// meaning: it incremented `totalAnimaLost` from EVERY source, and
// Bloody Hell fires at 45 of them. Z4Z Apocalypse dims its own
// controller 8 at a time. Six activations and a seat won a Solrend by
// bleeding itself, having touched the adversary exactly never.
//
// So the counters are split by WHO DID IT, and the classification is
// made at the call site because only the call site knows:
//
//   ANIMA_LOSS_KINDS.ADVERSARY  → totalAnimaLost / animaLostThisTurn
//       The PROPHECY LEDGER. An adversary's Lyt Attack, Anima strike,
//       Tome, Impera, U'hlti, Curio, stamp, a reflected attack, the
//       Prophet's Toll. Anything the other side did to you.
//
//   ANIMA_LOSS_KINDS.SELF       → totalAnimaSelfDimmed /
//                                 animaSelfDimmedThisTurn
//       A card cost, a self-dim effect, the Graveshift fare, a
//       Devotion, a search or resurrection fare — every voluntary
//       payment. Recorded because it is real; read by no Prophecy,
//       ever.
//
// ⚠ THE DEFAULT IS ADVERSARIAL, AND THAT IS A DELIBERATE CHOICE WITH A
// COST. Most of the ~30 call sites are damage, so defaulting the other
// way would have needed a flag on nearly all of them and a missed one
// would silently STOP counting a real hit — a Prophecy that never
// fires. Defaulting adversarial means a missed SELF site silently
// over-counts instead, which shows up as a Prophecy firing early. Both
// are bugs; this one is the loud one. Every site was audited by hand
// when the split was written. If you add a self-dim, tag it.
//
// ⚠ IF YOU ADD A PROPHECY that reads Anima loss, read `totalAnimaLost`
// / `animaLostThisTurn` and NOTHING ELSE. The self ledger exists to be
// displayed and to be honest, not to be won with.
export function buildAnimaLossUpdate(player, amount, kind = ANIMA_LOSS_KINDS.ADVERSARY) {
  if (!animaLossFeedsProphecy(kind)) {
    return {
      totalAnimaSelfDimmed: (player?.totalAnimaSelfDimmed || 0) + amount,
      animaSelfDimmedThisTurn: (player?.animaSelfDimmedThisTurn || 0) + amount,
    };
  }
  return {
    totalAnimaLost: (player?.totalAnimaLost || 0) + amount,
    animaLostThisTurn: (player?.animaLostThisTurn || 0) + amount,
  };
}

// Sugar for the self leg, so a call site can read as an English
// sentence instead of a third positional argument. Identical result.
export function buildSelfDimUpdate(player, amount) {
  return buildAnimaLossUpdate(player, amount, ANIMA_LOSS_KINDS.SELF);
}

// Call when a player GAINS Anima (heal, card effect, etc.)
export function buildAnimaGainUpdate(player, amount) {
  return {
    animaGainedThisTurn: (player?.animaGainedThisTurn || 0) + amount,
  };
}

// Call at end of each half-turn to rotate trackers.
// R100: the SELF ledger rotates alongside the adversarial one — both
// "this turn" counters are per-half-turn and both must clear, or the
// self counter becomes a duel-long total wearing a per-turn name,
// which is the exact bug turnEngine.js was written to fix for the
// gain counters. The two TOTALS are untouched here: Bloody Hell reads
// totalAnimaLost across the whole Solrend.
export function buildEndOfHalfTurnUpdate(player) {
  return {
    animaGainedLastTurn: player?.animaGainedThisTurn || 0,
    animaGainedThisTurn: 0,
    animaLostThisTurn: 0,
    animaSelfDimmedThisTurn: 0,
    // ── R119 · ONE WARDEN ART PER AENARCHIST PER TURN ─────────
    // caP'n, 2026-09-07: *"Each Aenarchist can only use 1 of their Warden
    // Primeras per turn."* It is per SEAT, not per being, and it spans
    // both of the Mind rift's options — fire the hand one and the
    // Grimoire one is shut until the seat's next Turn.
    //
    // ⚠ IT RESETS *HERE* AND NOWHERE ELSE, and this file's own history is
    // the argument for why. This function had exactly one caller — the END
    // TURN button — so a Turn that ran out on the timer never rotated, and
    // the NPC seat never rotated at all, in any driver. Measured on a real
    // duel: 24 of 24 hand-offs left the outgoing counter non-zero. A
    // once-per-Turn counter that never resets is not a once-per-Turn
    // counter, it is a once-per-DUEL counter wearing the wrong name, and
    // it fails in the direction that makes the game LOOK fine for one Turn.
    //
    // ⚠ AND ONLY THIS ONE ROTATES. The other new clocks in this wave —
    // Exiliation's two full Turns, the Ritual's three, each rift Art's own
    // cadence — are "last used on half-turn N" STAMPS compared against
    // match.turn, not per-Turn counters. Rotating a stamp here would reset
    // every cooldown in the game at every half-turn boundary and quietly
    // delete four separate cadences. Counters reset; stamps do not.
    wardenArtsThisTurn: 0,
  };
}